/**
 * Team Management Modal Component
 * 
 * Business 플랜용 팀 관리 모달
 * - 팀 생성/조회
 * - 팀원 초대/제거
 * - 역할 관리
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth';
import { useLanguage } from '../i18n';
import {
  getTeam,
  createTeam,
  inviteTeamMember,
  removeTeamMember,
  cancelInvite,
  updateMemberRole,
  leaveTeam,
  deleteTeam,
  createExtraSeatsCheckout,
  transferOwnership,
  type TeamDetailResponse,
  type TeamMember,
  type TeamInvite,
} from '../api';

interface TeamManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ConfirmAction = 'remove' | 'leave' | 'delete' | 'transfer' | null;

export default function TeamManagementModal({ isOpen, onClose }: TeamManagementModalProps) {
  const { t } = useLanguage();
  const { user, refreshUser, quota } = useAuth();
  
  // 팀 데이터
  const [teamData, setTeamData] = useState<TeamDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 팀 생성 폼
  const [teamName, setTeamName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  // 초대 폼
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'manager' | 'member'>('member');
  const [isInviting, setIsInviting] = useState(false);
  
  // 리드 양도
  const [transferTargetId, setTransferTargetId] = useState<number | null>(null);
  
  // 확인 다이얼로그
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [confirmTargetId, setConfirmTargetId] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // 성공 메시지
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // 추가 좌석 구매
  const [isPurchasingSeats, setIsPurchasingSeats] = useState(false);
  
  // 팀 데이터 로드
  const loadTeam = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getTeam();
      setTeamData(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load team');
    } finally {
      setIsLoading(false);
    }
  }, [user]);
  
  useEffect(() => {
    if (isOpen && user) {
      loadTeam();
    }
  }, [isOpen, user, loadTeam]);
  
  // 성공 메시지 자동 숨김
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);
  
  if (!isOpen || !user) return null;
  
  // 팀 플랜 여부: quota.plan이 team이거나 팀에 소속되어 있는 경우
  // (개발용 플랜 오버라이드 적용을 위해 quota 사용)
  const isTeamPlan = quota?.plan === 'team' || quota?.team !== null;
  const isOwner = teamData?.team.owner_id === user.id;
  const currentMember = teamData?.members.find(m => m.user_id === user.id);
  const isAdmin = currentMember?.role === 'owner' || currentMember?.role === 'manager';
  
  // 다운그레이드 상태: 팀 오너의 구독이 Team이 아닌 경우
  // API 응답의 is_frozen 값 사용 (백엔드에서 오너 플랜 확인)
  const isTeamFrozen = teamData?.team.is_frozen ?? false;
  const hasExistingTeam = teamData !== null;
  
  // 팀 생성
  const handleCreateTeam = async () => {
    if (!teamName.trim()) return;
    
    setIsCreating(true);
    setError(null);
    
    try {
      await createTeam(teamName.trim());
      setSuccessMessage(t.team.createTeam + ' ✓');
      setTeamName('');
      await loadTeam();
      await refreshUser?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create team');
    } finally {
      setIsCreating(false);
    }
  };
  
  // 멤버 초대
  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    
    setIsInviting(true);
    setError(null);
    
    try {
      await inviteTeamMember(inviteEmail.trim(), inviteRole);
      setSuccessMessage(t.team.inviteSent);
      setInviteEmail('');
      await loadTeam();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to invite');
    } finally {
      setIsInviting(false);
    }
  };
  
  // 확인 액션 실행
  const executeConfirmAction = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      switch (confirmAction) {
        case 'remove':
          if (confirmTargetId) {
            await removeTeamMember(confirmTargetId);
            setSuccessMessage(t.team.memberRemoved);
          }
          break;
        case 'leave':
          await leaveTeam();
          setSuccessMessage(t.team.leftTeam);
          await refreshUser?.();
          break;
        case 'delete':
          await deleteTeam();
          setSuccessMessage(t.team.teamDeleted);
          await refreshUser?.();
          break;
        case 'transfer':
          if (transferTargetId) {
            await transferOwnership(transferTargetId);
            setSuccessMessage(t.team.ownershipTransferred);
            await refreshUser?.();
          }
          break;
      }
      await loadTeam();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setIsProcessing(false);
      setConfirmAction(null);
      setConfirmTargetId(null);
      setTransferTargetId(null);
    }
  };
  
  // 초대 취소
  const handleCancelInvite = async (inviteId: number) => {
    setError(null);
    try {
      await cancelInvite(inviteId);
      setSuccessMessage(t.team.inviteCancelled);
      await loadTeam();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel invite');
    }
  };
  
  // 역할 변경
  const handleRoleChange = async (memberId: number, newRole: 'manager' | 'member') => {
    setError(null);
    try {
      await updateMemberRole(memberId, newRole);
      setSuccessMessage(t.team.roleChanged);
      await loadTeam();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to change role');
    }
  };
  
  // 추가 좌석 구매
  const handlePurchaseSeats = async () => {
    if (!teamData) return;
    
    setIsPurchasingSeats(true);
    setError(null);
    
    try {
      const response = await createExtraSeatsCheckout(teamData.team.id);
      // 체크아웃 페이지로 이동
      window.location.href = response.checkout_url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create checkout');
      setIsPurchasingSeats(false);
    }
  };
  
  // 역할 라벨
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return t.team.roleLead;
      case 'manager': return t.team.roleManager;
      default: return t.team.roleMember;
    }
  };
  
  // 리드 양도 대상 (오너가 아닌 멤버들)
  const transferCandidates = teamData?.members.filter(m => m.role !== 'owner') || [];
  const transferTarget = transferCandidates.find(m => m.user_id === transferTargetId);
  
  // 남은 일수 계산
  const getDaysUntilExpiry = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };
  
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-terminal-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-terminal-text">
            {t.team.title}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-terminal-muted hover:text-terminal-text transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* 컨텐츠 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 에러 메시지 */}
          {error && (
            <div className="p-3 bg-status-nogo/10 border border-status-nogo/30 rounded-lg text-status-nogo text-sm">
              {error}
            </div>
          )}
          
          {/* 성공 메시지 */}
          {successMessage && (
            <div className="p-3 bg-status-go/10 border border-status-go/30 rounded-lg text-status-go text-sm">
              {successMessage}
            </div>
          )}
          
          {/* Team 플랜 아닌 경우 - 팀도 없는 경우 (로딩 중에는 표시 안함) */}
          {!isLoading && !isTeamPlan && !hasExistingTeam && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-status-holding/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-status-holding" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-terminal-text mb-2">
                {t.team.teamRequired}
              </h3>
              <p className="text-terminal-muted text-sm">
                {t.team.teamRequiredDesc}
              </p>
            </div>
          )}
          
          {/* 다운그레이드 상태: 팀이 있지만 Team이 아닌 경우 */}
          {isTeamFrozen && (
            <div className="p-4 bg-status-holding/10 border border-status-holding/30 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-status-holding/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-status-holding" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-status-holding">
                    {t.downgrade.teamFrozen}
                  </h4>
                  <p className="text-xs text-terminal-muted mt-1">
                    {isOwner ? t.downgrade.teamFrozenOwnerDesc : t.downgrade.teamFrozenDesc}
                  </p>
                  <p className="text-xs text-terminal-muted mt-1 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 text-status-go" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t.downgrade.teamDataPreserved}
                  </p>
                  {isOwner && (
                    <button
                      onClick={onClose}
                      className="mt-3 px-4 py-2 bg-status-holding/20 hover:bg-status-holding/30 border border-status-holding/50 text-status-holding rounded-lg text-sm font-medium transition-colors"
                    >
                      {t.downgrade.upgradePlan}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* 로딩 */}
          {(isTeamPlan || hasExistingTeam) && isLoading && (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-status-go/30 border-t-status-go rounded-full animate-spin mx-auto"></div>
            </div>
          )}
          
          {/* 팀 없음 - 생성 폼 (Team인 경우만) */}
          {isTeamPlan && !isLoading && !teamData && (
            <div className="space-y-6">
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-terminal-dim/30 flex items-center justify-center">
                  <svg className="w-8 h-8 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-terminal-text mb-2">
                  {t.team.noTeam}
                </h3>
                <p className="text-terminal-muted text-sm mb-6">
                  {t.team.noTeamDesc}
                </p>
              </div>
              
              {/* 팀 생성 폼 */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-terminal-text">
                  {t.team.createTeam}
                </h4>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder={t.team.teamNamePlaceholder}
                    className="flex-1 px-3 py-2.5 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors"
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateTeam()}
                  />
                  <button
                    onClick={handleCreateTeam}
                    disabled={isCreating || !teamName.trim()}
                    className="px-4 py-2.5 bg-status-go/20 hover:bg-status-go/30 border border-status-go/50 text-status-go rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreating ? t.team.creating : t.team.create}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* 팀 정보 (Team 또는 기존 팀이 있는 경우) */}
          {(isTeamPlan || hasExistingTeam) && !isLoading && teamData && (
            <div className="space-y-6">
              {/* 팀 헤더 */}
              <div>
                <h3 className="text-xl font-semibold text-terminal-text">
                  {teamData.team.name}
                </h3>
                <p className="text-sm text-terminal-muted">
                  {t.team.memberCount
                    .replace('{count}', String(teamData.team.member_count))
                    .replace('{max}', String(teamData.team.max_members))}
                </p>
              </div>
              
              {/* 멤버 목록 */}
              <div>
                <h4 className="text-sm font-medium text-terminal-text mb-3">
                  {t.team.members}
                </h4>
                <div className="space-y-2">
                  {/* 정렬: 오너(리드) > 매니저 > 멤버 순 */}
                  {[...teamData.members]
                    .sort((a, b) => {
                      const roleOrder = { owner: 0, manager: 1, member: 2 };
                      return (roleOrder[a.role as keyof typeof roleOrder] ?? 2) - (roleOrder[b.role as keyof typeof roleOrder] ?? 2);
                    })
                    .map((member) => (
                    <MemberItem
                      key={member.id}
                      member={member}
                      isOwner={isOwner}
                      isFrozen={isTeamFrozen}
                      currentUserId={user.id}
                      onRoleChange={handleRoleChange}
                      onRemove={() => {
                        setConfirmTargetId(member.id);
                        setConfirmAction('remove');
                      }}
                      getRoleLabel={getRoleLabel}
                      t={t}
                    />
                  ))}
                </div>
              </div>
              
              {/* 대기 중인 초대 */}
              {teamData.pending_invites.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-terminal-text mb-3">
                    {t.team.pendingInvites}
                  </h4>
                  <div className="space-y-2">
                    {teamData.pending_invites.map((invite) => (
                      <InviteItem
                        key={invite.id}
                        invite={invite}
                        isAdmin={isAdmin}
                        isFrozen={isTeamFrozen}
                        onCancel={() => handleCancelInvite(invite.id)}
                        getDaysUntilExpiry={getDaysUntilExpiry}
                        getRoleLabel={getRoleLabel}
                        t={t}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* 초대 폼 - frozen 상태에서는 비활성화 */}
              {isAdmin && !isTeamFrozen && teamData.team.member_count < teamData.team.max_members && (
                <div>
                  <h4 className="text-sm font-medium text-terminal-text mb-3">
                    {t.team.inviteMember}
                  </h4>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder={t.team.emailPlaceholder}
                      className="flex-1 px-3 py-2 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors"
                      onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                    />
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as 'manager' | 'member')}
                      className="px-3 py-2 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm focus:outline-none focus:border-status-go/50 transition-colors cursor-pointer"
                    >
                      <option value="member">{t.team.roleMember}</option>
                      <option value="manager">{t.team.roleManager}</option>
                    </select>
                    <button
                      onClick={handleInvite}
                      disabled={isInviting || !inviteEmail.trim()}
                      className="px-4 py-2 bg-status-go/20 hover:bg-status-go/30 border border-status-go/50 text-status-go rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isInviting ? t.team.inviting : t.team.invite}
                    </button>
                  </div>
                </div>
              )}
              
              {/* 좌석 추가 구매 - 오너이고 최대 인원에 가까울 때 표시 (frozen 상태에서는 비활성화) */}
              {isOwner && !isTeamFrozen && teamData.team.member_count >= teamData.team.max_members - 1 && (
                <div className="p-4 bg-status-conditional/10 border border-status-conditional/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-terminal-text flex items-center gap-2">
                        <svg className="w-4 h-4 text-status-conditional" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t.team.addSeats}
                      </h4>
                      <p className="text-xs text-terminal-muted mt-1">
                        {teamData.team.max_members >= 50 
                          ? t.team.maxSeatsReached 
                          : t.team.addSeatsDesc}
                      </p>
                    </div>
                    {teamData.team.max_members < 50 && (
                      <button
                        onClick={handlePurchaseSeats}
                        disabled={isPurchasingSeats}
                        className="px-4 py-2 bg-status-conditional/20 hover:bg-status-conditional/30 border border-status-conditional/50 text-status-conditional rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
                      >
                        {isPurchasingSeats ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            {t.team.purchasing}
                          </>
                        ) : (
                          <>
                            {t.team.seatsPerPurchase}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* 리드 양도 (오너이고 다른 멤버가 있는 경우만) */}
              {isOwner && !isTeamFrozen && transferCandidates.length > 0 && (
                <div className="p-4 bg-terminal-dim/10 border border-terminal-border rounded-lg">
                  <h4 className="text-sm font-medium text-terminal-text mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-status-holding" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    {t.team.transferOwnership}
                  </h4>
                  <p className="text-xs text-terminal-muted mb-3">
                    {t.team.transferOwnershipDesc}
                  </p>
                  <div className="flex gap-2">
                    <select
                      value={transferTargetId || ''}
                      onChange={(e) => setTransferTargetId(e.target.value ? Number(e.target.value) : null)}
                      className="flex-1 px-3 py-2 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm focus:outline-none focus:border-status-holding/50 transition-colors cursor-pointer"
                    >
                      <option value="">{t.team.transferTo}</option>
                      {transferCandidates.map((m) => (
                        <option key={m.user_id} value={m.user_id}>
                          {m.name || m.email} ({getRoleLabel(m.role)})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => setConfirmAction('transfer')}
                      disabled={!transferTargetId}
                      className="px-4 py-2 bg-status-holding/20 hover:bg-status-holding/30 border border-status-holding/50 text-status-holding rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {t.team.transferOwnership}
                    </button>
                  </div>
                </div>
              )}
              
              {/* 팀 나가기 / 삭제 */}
              <div className="pt-4 border-t border-terminal-border">
                {isOwner ? (
                  <button
                    onClick={() => setConfirmAction('delete')}
                    className="w-full py-2.5 text-status-nogo/70 hover:text-status-nogo text-sm transition-colors"
                  >
                    {t.team.deleteTeam}
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirmAction('leave')}
                    className="w-full py-2.5 text-status-holding/70 hover:text-status-holding text-sm transition-colors"
                  >
                    {t.team.leaveTeam}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* 확인 다이얼로그 */}
        {confirmAction && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center p-6">
            <div className="bg-terminal-bg border border-terminal-border rounded-xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-terminal-text mb-2">
                {confirmAction === 'delete' ? t.team.confirmDelete : 
                 confirmAction === 'transfer' ? t.team.confirmTransfer : t.team.confirm}
              </h3>
              <p className="text-sm text-terminal-muted mb-4">
                {confirmAction === 'remove' && t.team.confirmRemove}
                {confirmAction === 'leave' && t.team.confirmLeave}
                {confirmAction === 'delete' && t.team.confirmDeleteDesc}
                {confirmAction === 'transfer' && transferTarget && 
                  t.team.confirmTransferDesc.replace('{name}', transferTarget.name || transferTarget.email)}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setConfirmAction(null);
                    setConfirmTargetId(null);
                  }}
                  disabled={isProcessing}
                  className="flex-1 py-2.5 border border-terminal-border rounded-lg text-terminal-muted text-sm hover:bg-terminal-dim/30 transition-colors disabled:opacity-50"
                >
                  {t.team.cancel}
                </button>
                <button
                  onClick={executeConfirmAction}
                  disabled={isProcessing}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                    confirmAction === 'delete'
                      ? 'bg-status-nogo hover:bg-status-nogo/90 text-white'
                      : confirmAction === 'transfer'
                        ? 'bg-status-holding hover:bg-status-holding/90 text-white'
                        : 'bg-status-holding hover:bg-status-holding/90 text-white'
                  }`}
                >
                  {isProcessing ? '...' : t.team.confirm}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// 멤버 아이템 컴포넌트
function MemberItem({
  member,
  isOwner,
  isFrozen,
  currentUserId,
  onRoleChange,
  onRemove,
  getRoleLabel,
  t,
}: {
  member: TeamMember;
  isOwner: boolean;
  isFrozen?: boolean;
  currentUserId: number;
  onRoleChange: (memberId: number, role: 'manager' | 'member') => void;
  onRemove: () => void;
  getRoleLabel: (role: string) => string;
  t: ReturnType<typeof useLanguage>['t'];
}) {
  const isSelf = member.user_id === currentUserId;
  // frozen 상태에서는 관리 기능 비활성화
  const canManage = isOwner && !isSelf && member.role !== 'owner' && !isFrozen;
  
  return (
    <div className="flex items-center gap-3 p-3 bg-terminal-dim/20 border border-terminal-border rounded-lg">
      {/* 아바타 */}
      <div className="w-10 h-10 rounded-full bg-terminal-dim flex items-center justify-center flex-shrink-0 overflow-hidden">
        {member.picture ? (
          <img src={member.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="text-terminal-muted text-sm font-medium">
            {(member.name || member.email)[0].toUpperCase()}
          </span>
        )}
      </div>
      
      {/* 정보 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-terminal-text truncate">
            {member.name || member.email.split('@')[0]}
          </span>
          {isSelf && (
            <span className="text-xs text-terminal-muted">(you)</span>
          )}
        </div>
        <p className="text-xs text-terminal-muted truncate">
          {member.email}
        </p>
      </div>
      
      {/* 역할 */}
      {canManage ? (
        <select
          value={member.role}
          onChange={(e) => onRoleChange(member.id, e.target.value as 'manager' | 'member')}
          className="px-2 py-1 bg-terminal-dim/30 border border-terminal-border rounded text-terminal-text text-xs focus:outline-none focus:border-status-go/50 cursor-pointer"
        >
          <option value="member">{t.team.roleMember}</option>
          <option value="manager">{t.team.roleManager}</option>
        </select>
      ) : (
        <span className={`px-2 py-1 text-xs rounded ${
          member.role === 'owner' 
            ? 'bg-status-go/20 text-status-go'
            : member.role === 'manager'
              ? 'bg-status-conditional/20 text-status-conditional'
              : 'bg-terminal-dim/30 text-terminal-muted'
        }`}>
          {getRoleLabel(member.role)}
        </span>
      )}
      
      {/* 제거 버튼 */}
      {canManage && (
        <button
          onClick={onRemove}
          className="p-1.5 text-terminal-muted hover:text-status-nogo transition-colors"
          title={t.team.removeMember}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// 초대 아이템 컴포넌트
function InviteItem({
  invite,
  isAdmin,
  isFrozen,
  onCancel,
  getDaysUntilExpiry,
  getRoleLabel,
  t,
}: {
  invite: TeamInvite;
  isAdmin: boolean;
  isFrozen?: boolean;
  onCancel: () => void;
  getDaysUntilExpiry: (expiresAt: string) => number;
  getRoleLabel: (role: string) => string;
  t: ReturnType<typeof useLanguage>['t'];
}) {
  const daysLeft = getDaysUntilExpiry(invite.expires_at);
  // frozen 상태에서는 취소 버튼 비활성화
  const canCancel = isAdmin && !isFrozen;
  
  return (
    <div className="flex items-center gap-3 p-3 bg-terminal-dim/10 border border-terminal-border/50 rounded-lg opacity-80">
      {/* 아이콘 */}
      <div className="w-10 h-10 rounded-full bg-terminal-dim/50 flex items-center justify-center flex-shrink-0">
        <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      
      {/* 정보 */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-terminal-muted truncate">
          {invite.email}
        </p>
        <p className="text-xs text-terminal-muted/70">
          {invite.is_expired 
            ? t.team.expired
            : t.team.expires.replace('{days}', String(daysLeft))}
        </p>
      </div>
      
      {/* 역할 */}
      <span className="px-2 py-1 bg-terminal-dim/30 text-terminal-muted text-xs rounded">
        {getRoleLabel(invite.role)}
      </span>
      
      {/* 취소 버튼 */}
      {canCancel && (
        <button
          onClick={onCancel}
          className="p-1.5 text-terminal-muted hover:text-status-nogo transition-colors"
          title={t.team.cancelInvite}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

